/**
 * Performance Enhancement Suite
 * Advanced optimization utilities for better user experience
 */

class PerformanceEnhancer {
  constructor() {
    this.domCache = new Map();
    this.eventListeners = new Map();
    this.resizeObserver = null;
    this.intersectionObservers = new Map();
    this.initialized = false;
    
    // Performance monitoring
    this.metrics = {
      scriptLoads: new Map(),
      domQueries: 0,
      eventFires: new Map()
    };
  }

  /**
   * Enhanced debounce with performance monitoring
   */
  static debounce(func, wait, options = {}) {
    const { immediate = false, maxWait = null, leading = false } = options;
    let timeoutId, maxTimeoutId, lastCallTime, lastInvokeTime = 0;

    function invokeFunc(time) {
      const args = lastArgs;
      const thisArg = lastThis;
      
      lastArgs = lastThis = undefined;
      lastInvokeTime = time;
      return func.apply(thisArg, args);
    }

    function leadingEdge(time) {
      lastInvokeTime = time;
      timeoutId = setTimeout(timerExpired, wait);
      return leading ? invokeFunc(time) : result;
    }

    function remainingWait(time) {
      const timeSinceLastCall = time - lastCallTime;
      const timeSinceLastInvoke = time - lastInvokeTime;
      const timeWaiting = wait - timeSinceLastCall;
      
      return maxWait ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
    }

    function shouldInvoke(time) {
      const timeSinceLastCall = time - lastCallTime;
      const timeSinceLastInvoke = time - lastInvokeTime;
      
      return (lastCallTime === undefined || 
              (timeSinceLastCall >= wait) || 
              (timeSinceLastCall < 0) || 
              (maxWait && timeSinceLastInvoke >= maxWait));
    }

    function timerExpired() {
      const time = Date.now();
      if (shouldInvoke(time)) {
        return trailingEdge(time);
      }
      timeoutId = setTimeout(timerExpired, remainingWait(time));
    }

    function trailingEdge(time) {
      timeoutId = undefined;
      
      if (trailing && lastArgs) {
        return invokeFunc(time);
      }
      lastArgs = lastThis = undefined;
      return result;
    }

    let lastArgs, lastThis, result;
    const trailing = !immediate;

    function debounced(...args) {
      const time = Date.now();
      const isInvoking = shouldInvoke(time);
      
      lastArgs = args;
      lastThis = this;
      lastCallTime = time;
      
      if (isInvoking) {
        if (timeoutId === undefined) {
          return leadingEdge(lastCallTime);
        }
        if (maxWait) {
          timeoutId = setTimeout(timerExpired, wait);
          return invokeFunc(lastCallTime);
        }
      }
      if (timeoutId === undefined) {
        timeoutId = setTimeout(timerExpired, wait);
      }
      return result;
    }

    debounced.cancel = function() {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      if (maxTimeoutId !== undefined) {
        clearTimeout(maxTimeoutId);
      }
      lastInvokeTime = 0;
      lastArgs = lastCallTime = lastThis = timeoutId = maxTimeoutId = undefined;
    };

    return debounced;
  }

  /**
   * Enhanced throttle with performance tracking
   */
  static throttle(func, limit, options = {}) {
    const { leading = true, trailing = true } = options;
    let inThrottle, lastFunc, lastRan;

    return function throttled(...args) {
      if (!inThrottle) {
        if (leading) func.apply(this, args);
        lastRan = Date.now();
        inThrottle = true;
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(() => {
          if (Date.now() - lastRan >= limit) {
            if (trailing) func.apply(this, args);
            lastRan = Date.now();
            inThrottle = false;
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  }

  /**
   * Cached DOM query with performance monitoring
   */
  cachedQuery(selector, context = document, force = false) {
    this.metrics.domQueries++;
    
    const key = `${selector}:${context === document ? 'document' : 'context'}`;
    
    if (!force && this.domCache.has(key)) {
      return this.domCache.get(key);
    }
    
    const elements = context.querySelectorAll(selector);
    this.domCache.set(key, elements);
    
    return elements;
  }

  /**
   * Invalidate cached queries when DOM changes
   */
  invalidateCache(selector = null) {
    if (selector) {
      const keysToDelete = Array.from(this.domCache.keys())
        .filter(key => key.startsWith(selector));
      keysToDelete.forEach(key => this.domCache.delete(key));
    } else {
      this.domCache.clear();
    }
  }

  /**
   * Optimized event delegation with automatic cleanup
   */
  delegateEvent(parent, selector, event, handler, options = {}) {
    const key = `${parent}:${selector}:${event}`;
    
    // Remove existing listener if any
    if (this.eventListeners.has(key)) {
      this.removeDelegatedEvent(key);
    }
    
    const delegatedHandler = (e) => {
      const target = e.target.closest(selector);
      if (target && parent.contains(target)) {
        this.metrics.eventFires.set(event, (this.metrics.eventFires.get(event) || 0) + 1);
        handler.call(target, e);
      }
    };
    
    parent.addEventListener(event, delegatedHandler, options);
    this.eventListeners.set(key, { parent, event, handler: delegatedHandler });
    
    return key;
  }

  /**
   * Remove delegated event listener
   */
  removeDelegatedEvent(key) {
    const listener = this.eventListeners.get(key);
    if (listener) {
      listener.parent.removeEventListener(listener.event, listener.handler);
      this.eventListeners.delete(key);
    }
  }

  /**
   * Optimized scroll handler with RAF
   */
  setupScrollOptimization() {
    let ticking = false;
    
    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          document.dispatchEvent(new CustomEvent('optimizedScroll', {
            detail: { scrollY: window.scrollY }
          }));
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', scrollHandler, { passive: true });
    return scrollHandler;
  }

  /**
   * Optimized resize handler with debouncing
   */
  setupResizeOptimization() {
    const resizeHandler = PerformanceEnhancer.debounce(() => {
      document.dispatchEvent(new CustomEvent('optimizedResize', {
        detail: { 
          width: window.innerWidth, 
          height: window.innerHeight 
        }
      }));
    }, 150);
    
    window.addEventListener('resize', resizeHandler, { passive: true });
    return resizeHandler;
  }

  /**
   * Intersection Observer pool for better performance
   */
  createIntersectionObserver(callback, options = {}) {
    const key = JSON.stringify(options);
    
    if (!this.intersectionObservers.has(key)) {
      const observer = new IntersectionObserver(callback, {
        rootMargin: '50px',
        threshold: 0.1,
        ...options
      });
      this.intersectionObservers.set(key, observer);
    }
    
    return this.intersectionObservers.get(key);
  }

  /**
   * Lazy image loading with modern techniques
   */
  setupLazyLoading() {
    // Use native lazy loading when supported
    if ('loading' in HTMLImageElement.prototype) {
      document.querySelectorAll('img[data-src]').forEach(img => {
        img.loading = 'lazy';
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    } else {
      // Fallback to intersection observer
      const lazyImages = document.querySelectorAll('img[data-src]');
      if (lazyImages.length > 0) {
        const imageObserver = this.createIntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          });
        });
        
        lazyImages.forEach(img => imageObserver.observe(img));
      }
    }
  }

  /**
   * Memory-efficient animation frame handling
   */
  animationFrameManager() {
    const callbacks = new Set();
    let rafId = null;
    
    const tick = () => {
      callbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('Animation callback error:', error);
        }
      });
      
      if (callbacks.size > 0) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };
    
    return {
      add(callback) {
        callbacks.add(callback);
        if (!rafId) {
          rafId = requestAnimationFrame(tick);
        }
      },
      
      remove(callback) {
        callbacks.delete(callback);
      },
      
      clear() {
        callbacks.clear();
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      }
    };
  }

  /**
   * Critical resource loading optimization
   */
  optimizeCriticalResources() {
    // Preload critical fonts
    const criticalFonts = [
      '/assets/fonts/primary-font.woff2',
      '/assets/fonts/secondary-font.woff2'
    ];
    
    criticalFonts.forEach(font => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'font';
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
      link.href = font;
      document.head.appendChild(link);
    });
    
    // Prefetch likely navigation targets
    const prefetchLinks = document.querySelectorAll('a[href^="/"]');
    prefetchLinks.forEach((link, index) => {
      if (index < 3) { // Limit prefetch to first 3 internal links
        const prefetchLink = document.createElement('link');
        prefetchLink.rel = 'prefetch';
        prefetchLink.href = link.href;
        document.head.appendChild(prefetchLink);
      }
    });
  }

  /**
   * Performance monitoring and reporting
   */
  setupPerformanceMonitoring() {
    // Web Vitals monitoring
    if ('PerformanceObserver' in window) {
      // Monitor Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        console.log('LCP:', lastEntry.startTime);
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      
      // Monitor First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          console.log('FID:', entry.processingStart - entry.startTime);
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });
    }
  }

  /**
   * Initialize all performance enhancements
   */
  init() {
    if (this.initialized) return;
    
    console.log('🚀 Initializing performance enhancements...');
    
    // Setup optimized event handlers
    this.setupScrollOptimization();
    this.setupResizeOptimization();
    
    // Setup lazy loading
    this.setupLazyLoading();
    
    // Optimize critical resources
    this.optimizeCriticalResources();
    
    // Setup performance monitoring
    this.setupPerformanceMonitoring();
    
    // Setup animation frame manager
    this.rafManager = this.animationFrameManager();
    
    // Global event delegation for common interactions
    this.setupGlobalDelegation();
    
    this.initialized = true;
    console.log('✓ Performance enhancements initialized');
  }

  /**
   * Setup global event delegation for common patterns
   */
  setupGlobalDelegation() {
    // Form interactions
    this.delegateEvent(document.body, 'form', 'submit', (e) => {
      const form = e.target;
      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.classList.add('loading');
      }
    });
    
    // Button interactions
    this.delegateEvent(document.body, 'button', 'click', (e) => {
      const button = e.target;
      if (button.dataset.loading !== 'false') {
        button.classList.add('clicked');
        setTimeout(() => button.classList.remove('clicked'), 150);
      }
    });
    
    // Link prefetching on hover
    this.delegateEvent(document.body, 'a[href^="/"]', 'mouseenter', (e) => {
      const link = e.target;
      if (!link.dataset.prefetched) {
        const prefetchLink = document.createElement('link');
        prefetchLink.rel = 'prefetch';
        prefetchLink.href = link.href;
        document.head.appendChild(prefetchLink);
        link.dataset.prefetched = 'true';
      }
    }, { once: true });
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    return {
      domQueries: this.metrics.domQueries,
      eventFires: Object.fromEntries(this.metrics.eventFires),
      cacheSize: this.domCache.size,
      activeObservers: this.intersectionObservers.size
    };
  }

  /**
   * Cleanup method
   */
  cleanup() {
    // Clear all event listeners
    this.eventListeners.forEach((_, key) => this.removeDelegatedEvent(key));
    
    // Clear intersection observers
    this.intersectionObservers.forEach(observer => observer.disconnect());
    this.intersectionObservers.clear();
    
    // Clear RAF manager
    if (this.rafManager) {
      this.rafManager.clear();
    }
    
    // Clear cache
    this.domCache.clear();
    
    console.log('🧹 Performance enhancer cleaned up');
  }
}

// Enhanced utilities extending existing functionality
window.PerformanceUtils = {
  debounce: PerformanceEnhancer.debounce,
  throttle: PerformanceEnhancer.throttle,
  
  // Memory-efficient DOM caching
  createDOMCache() {
    return window.performanceEnhancer?.domCache || new Map();
  },
  
  // Optimized event handling
  addOptimizedEventListener(element, event, handler, options = {}) {
    const optimizedHandler = options.debounce ? 
      PerformanceEnhancer.debounce(handler, options.debounce) :
      options.throttle ? 
      PerformanceEnhancer.throttle(handler, options.throttle) :
      handler;
    
    element.addEventListener(event, optimizedHandler, options);
    return optimizedHandler;
  },
  
  // Performance measurement
  measurePerformance(name, fn) {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    console.log(`${name}: ${end - start}ms`);
    return result;
  },
  
  async measureAsyncPerformance(name, fn) {
    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    console.log(`${name}: ${end - start}ms`);
    return result;
  }
};

// Initialize performance enhancer globally
window.performanceEnhancer = new PerformanceEnhancer();

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.performanceEnhancer.init();
  });
} else {
  window.performanceEnhancer.init();
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (window.performanceEnhancer) {
    window.performanceEnhancer.cleanup();
  }
});